<template>
  <div class="customs-service" v-if="showCustomsService">
    <!-- EU-related fields only -->
    <div>
      <!-- Departure Airport Field -->
      <div class="departure-airport mb-4">
        <p class="color-grey-3 font-smoothing fs-14 mb-2">Fra hvilken lufthavn starter din hjemrejse? (der hvor bagagen tjekkes ind). Kun lufthavne i EU*</p>
        <AutocompleteSelect
          v-model="selectedDepartureAirport"
          :options="filteredAirportsForCustoms"
          :label="airportLabel"
          track-by="iata"
          placeholder="f.eks: PMI eller Mallorca"
          :allow-empty="false"
          :show-suggestions-on-focus="true"
          :require-selection="true"
          selection-error-text="Vælg en lufthavn fra listen – teksten alene kan ikke bruges."
          no-results-text="Ingen lufthavn fundet. Prøv navn, by eller IATA-kode."
          @select="selectDepartureAirport"
          @clear="selectDepartureAirport(null)"
        />
      </div>

      <!-- Rest of the form displayed only if departure airport is selected -->
      <div v-if="selectedDepartureAirport">
        <!-- Transit Stops -->
        <div class="transit-stops mb-4">
                  <p class="color-grey-3 fs-14 mb-2">Har du transit i andre lufthavne inden du kommer til Danmark?</p>
        <div class="checkbox-group mb-3">
          <div class="checkbox-wrapper mb-2">
            <input 
              type="checkbox" 
              id="transit-yes" 
              :checked="hasTransitStops === true"
              @change="handleTransitYesChange"
            >
            <label for="transit-yes" class="fs-14 font-smoothing ml-2">
              Ja
            </label>
          </div>
          <div class="checkbox-wrapper mb-2">
            <input 
              type="checkbox" 
              id="transit-no" 
              :checked="hasTransitStops === false"
              @change="handleTransitNoChange"
            >
            <label for="transit-no" class="fs-14 font-smoothing ml-2">
              Nej
            </label>
          </div>
        </div>

          <!-- Show transit fields only if Ja is selected -->
          <div v-if="hasTransitStops === true">
            <div class="transit-stop mb-3">
              <p class="color-grey-3 font-smoothing fs-14 mb-2">Første transit lufthavn</p>
              <AutocompleteSelect
                v-model="selectedFirstTransitStop"
                :options="filteredAirportsForCustoms"
                :label="airportLabel"
                track-by="iata"
                placeholder="f.eks: PMI eller Mallorca"
                :allow-empty="true"
                :show-suggestions-on-focus="true"
                :require-selection="true"
                selection-error-text="Vælg en lufthavn fra listen – teksten alene kan ikke bruges."
                no-results-text="Ingen lufthavn fundet. Prøv navn, by eller IATA-kode."
                @select="selectFirstTransitStop"
                @clear="selectFirstTransitStop(null)"
              />
              <p v-if="hasTransitStops === true && !selectedFirstTransitStop" class="color-red fs-12 mt-2">Første transit lufthavn skal angives</p>
            </div>

            <!-- Additional form content only visible if first transit airport is selected -->
            <div v-if="selectedFirstTransitStop">
              <div class="additional-stops">
                <div v-for="(stop, index) in additionalTransitStops" :key="`transit-${index}`" class="transit-stop-item mb-3">
                  <div class="d-flex align-items-center mb-2">
                    <p class="color-grey-3 fs-14 mb-0 mr-3">Næste transit {{ index + 2 }}</p>
                    <button 
                      type="button" 
                      class="btn-remove-stop" 
                      @click="removeTransitStop(index)"
                    >
                      <i class="material-icons fs-16">close</i>
                    </button>
                  </div>
                  <AutocompleteSelect
                    v-model="selectedAdditionalTransitStops[index]"
                    :options="filteredAirportsForCustoms"
                    :label="airportLabel"
                    track-by="iata"
                    placeholder="f.eks: PMI eller Mallorca"
                    :allow-empty="true"
                    :show-suggestions-on-focus="true"
                    :require-selection="true"
                    selection-error-text="Vælg en lufthavn fra listen – teksten alene kan ikke bruges."
                    no-results-text="Ingen lufthavn fundet. Prøv navn, by eller IATA-kode."
                    @select="(selectedAirport) => selectAdditionalTransitStop(index, selectedAirport)"
                    @clear="selectAdditionalTransitStop(index, null)"
                  />
                </div>
                
                <div class="add-more-transit mb-3">
                  <p class="fs-14 font-smoothing mb-2">Hvis du har transit i andre lufthavne så tilføje dem her under</p>
                  <button 
                    type="button" 
                    class="btn-add-transit"
                    @click="addTransitStop"
                  >
                    <i class="material-icons fs-16 mr-1">add</i>
                    Tilføj endnu en transit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Age Distribution - shown only when selection is made -->
        <div class="age-distribution mb-4" v-if="hasTransitStops === false || (hasTransitStops === true && selectedFirstTransitStop)">
          <p class="color-grey-3 fs-16 mb-3">Antal rejsende (mindst 1 person skal angives):</p>
          <div class="d-flex flex-wrap">
            <div class="age-group mr-4 mb-3">
              <p class="color-grey-3 fs-14 mb-2">Antal rejsende under 16 år</p>
              <input 
                class="input-number" 
                type="number" 
                min="0" 
                v-model.number="travelersUnder16"
              >
            </div>
            
            <div class="age-group mr-4 mb-3">
              <p class="color-grey-3 fs-14 mb-2">Antal rejsende på 16 år</p>
              <input 
                class="input-number" 
                type="number" 
                min="0" 
                v-model.number="travelersAge16"
              >
            </div>
            
            <div class="age-group mr-4 mb-3">
              <p class="color-grey-3 fs-14 mb-2">Antal rejsende på 17 år</p>
              <input 
                class="input-number" 
                type="number" 
                min="0" 
                v-model.number="travelersAge17"
              >
            </div>
            
            <div class="age-group mb-3">
              <p class="color-grey-3 fs-14 mb-2">Antal rejsende på +18 år</p>
              <input 
                class="input-number" 
                type="number" 
                min="0" 
                v-model.number="travelersAge18Plus"
              >
            </div>
          </div>
          <p v-if="!isTravelersValid" class="color-red fs-12 mt-2">Mindst 1 rejsende skal angives</p>
        </div>

        <!-- Code Field - shown only when selection is made -->
        <div class="customs-code mb-4" v-if="hasTransitStops === false || (hasTransitStops === true && selectedFirstTransitStop)">
          <p class="color-grey-3 fs-14 mb-2">Kode til evt lås på bagagen</p>
          <input 
            class="input-default" 
            type="text" 
            v-model="customsCode"
            placeholder="Kode til bagagen"
          >
        </div>

        <!-- Customs Conditions - shown only when selection is made and travelers valid -->
        <div class="customs-conditions mb-4" v-if="(hasTransitStops === false || (hasTransitStops === true && selectedFirstTransitStop)) && isTravelersValid">
          <p class="fs-16 font-smoothing mb-3">Jeg (kunden og den rejsende, som har ansvaret for bagagen):</p>
          
          <ol class="customs-list fs-12 color-grey-3">
            <li class="mb-2">bekræfter hermed, at jeg er fuldt ud bekendt med og vil overholde restriktionerne i forbindelse med import af varer til Danmark, jf. Toldstyrelsens, https://skat.dk/borger/rejser/rejser-inden-for-eu</li>
            
            <li class="mb-2">bekræfter, at bagagen ikke indeholder forbudte eller begrænsede varer. Jeg bekræfter desuden, at bagagen (inkl. min håndbagage) ikke indeholder kontanter eller andre pengeinstrumenter svarende til over 10.000 EUR, jf. lov om forebyggelse i afvigtningshandelen i Danmark.</li>
            
            <li class="mb-2">bekræfter, at min bagage ikke indeholder tobaksvarer</li>
            
            <li class="mb-2">er klar over, at overtrædelse af importreglerne er strafbart i henhold til toldloven. Hvis told- og punktafgiftshøvnen overskrides, har Valizo som din repræsentant tilladt at orientere toldvæsenet, at indholdet i henvisning af retsplejereglerne kapitel 74 om beslaglæggelse. Er du uenig kan du tage kontakt til SKAT. Valizo kan ikke holdes ansvarlig ombeslaglæggelse.</li>
            
            <li class="mb-2">accepterer at Valizo ikke kan drages til ansvar hvis bagagen tilbageholdes af myndighederne, eller der skal betales en afgift/bøde. Ligesom Valizo ikke længere er ansvarlig for at ansvarserklæringen buden til pkt 1-9 og at ansvaret for andre overtrædelse udelukkende påhviler mig, og i det tilfælde er refusion og levering ikke er muligt.</li>
            
            <li class="mb-2">giver tilladelse til at Valizo må åbne min bagage hvis myndighederne kræver at gennemse bagagen. Kan bagagen ikke åbnes overgåder den til myndighederne (vf pkt. 5)</li>
            
            <li class="mb-2">bekræfter at Valizo ikke kan gøres ansvarligt for en ødeling bagage som modtages fra flyselskabet, dette vil være en sag mellem mig og flyselskabet</li>
            
            <li class="mb-2">bekræfter, at min rejse, inkl. transit, kun har foregået indenfor EU (dog ikke Kanariske øer, Grønland, Færøerne, Nordcypern)</li>
            
            <li class="mb-2">bekræfter at Valizo ikke kan gøres ansvarligt for en ødeling bagage som modtages fra flyselskabet, dette vil være en sag mellem mig og flyselskabet</li>
            
            <li class="mb-2">bekræfter at jeg vil fremstille billeder af bagage og flyselskabets bagvægtstag så bagagen kan identificeres korrekt. I modsat fald kan bagagen ikke kunne levnes og der vil være en afvisning.</li>
          </ol>
        </div>

        <!-- Acceptance Checkbox - shown only when selection is made and travelers valid -->
        <div class="customs-acceptance" v-if="(hasTransitStops === false || (hasTransitStops === true && selectedFirstTransitStop)) && isTravelersValid">
          <div class="checkbox-wrapper">
            <input 
              type="checkbox" 
              id="customs-acceptance" 
              v-model="customsAcceptance"
              required
              @change="$emit('acceptance-change', customsAcceptance)"
            >
            <label for="customs-acceptance" class="fs-14 font-smoothing ml-2">
              Jeg erklærer hermed at jeg alle informationerne herover er sande og fyldestgørende og at jeg indforstået med kravene i punkt 1-9 og at ansvaret for enhver overtrædelse udelukkende påhviler mig, og i det tilfælde er refusion og levering ikke er muligt.
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapFields } from 'vuex-map-fields'
import { getAirports } from "../assets/airports"
import { validateAirportsData } from "../utils/airportsVersionCheck"
import AutocompleteSelect from '@/components/AutocompleteSelect'

export default {
  name: 'CustomsService',
  
  props: {
    showCustomsService: {
      type: Boolean,
      default: false
    }
  },

  components: {
    AutocompleteSelect
  },

  data() {
    return {
      airports: [],
      euAirports: [],
      selectedDepartureAirport: null,
      selectedFirstTransitStop: null,
      selectedAdditionalTransitStops: [],
      travelType: 'eu' // Default to EU travel
    }
  },

  computed: {
    ...mapFields([
      'bookingHome.departureAirport',
      'bookingHome.hasTransitStops',
      'bookingHome.customsAcceptance',
      'bookingHome.travelersUnder16',
      'bookingHome.travelersAge16',
      'bookingHome.travelersAge17',
      'bookingHome.travelersAge18Plus',
      'bookingHome.customsCode',
      'bookingHome.customsComment'
    ]),

    firstTransitStop: {
      get() {
        return this.$store.state.bookingHome.transitStops[0] || '';
      },
      set(value) {
        const stops = [...this.$store.state.bookingHome.transitStops];
        stops[0] = value;
        this.$store.commit('updateTransitStops', stops);
      }
    },

    additionalTransitStops: {
      get() {
        // Start from index 1 (second transit stop onwards)
        const stops = this.$store.state.bookingHome.transitStops.slice(1);
        // Only show additional fields if they have been explicitly added
        return stops.length > 0 ? stops : [];
      },
      set(value) {
        const allStops = [this.firstTransitStop, ...value];
        this.$store.commit('updateTransitStops', allStops);
      }
    },

    totalTravelers() {
      return (this.travelersUnder16 || 0) + 
             (this.travelersAge16 || 0) + 
             (this.travelersAge17 || 0) + 
             (this.travelersAge18Plus || 0);
    },

    isTravelersValid() {
      return this.totalTravelers >= 1;
    },

    isTransitValid() {
      // If transit is checked (true), first transit airport must be selected
      if (this.hasTransitStops === true) {
        return this.selectedFirstTransitStop !== null;
      }
      return true; // If no transit (false) or nothing selected (null), it's valid
    },

    isFormValid() {
      // If nothing is selected (null), form is valid by default
      if (this.hasTransitStops === null) {
        return true;
      }
      
      // If Ja is selected, require transit fields
      if (this.hasTransitStops === true) {
        return this.isTravelersValid && this.isTransitValid && this.customsAcceptance;
      }
      
      // If Nej selected (false), require only travelers and acceptance
      if (this.hasTransitStops === false) {
        return this.isTravelersValid && this.customsAcceptance;
      }
      
      return true;
    },

    filteredAirportsForCustoms() {
      return this.euAirports;
    }
  },

  methods: {
    airportLabel: (item) => `${item.name}(${item.iata}), ${item.cityDK}, ${item.countryDK}`,

    handleTransitYesChange(event) {
      if (event.target.checked) {
        this.hasTransitStops = true;
      } else {
        this.hasTransitStops = null;
      }
    },

    handleTransitNoChange(event) {
      if (event.target.checked) {
        this.hasTransitStops = false;
      } else {
        this.hasTransitStops = null;
      }
    },

    addTransitStop() {
      const allStops = [this.firstTransitStop, ...this.additionalTransitStops, ''];
      this.$store.commit('updateTransitStops', allStops);
      
      const currentSelectedStops = [...this.selectedAdditionalTransitStops];
      currentSelectedStops.push(null);
      this.selectedAdditionalTransitStops = currentSelectedStops;
    },

    removeTransitStop(index) {
      const allStops = [this.firstTransitStop, ...this.additionalTransitStops];
      allStops.splice(index + 1, 1); // +1 because we start from first transit stop
      this.$store.commit('updateTransitStops', allStops);
      
      const currentSelectedStops = [...this.selectedAdditionalTransitStops];
      currentSelectedStops.splice(index, 1);
      this.selectedAdditionalTransitStops = currentSelectedStops;
    },

    selectDepartureAirport(selectedAirport) {
      this.departureAirport = selectedAirport ? selectedAirport.iata : '';
    },

    selectFirstTransitStop(selectedAirport) {
      const stops = [...this.$store.state.bookingHome.transitStops];
      stops[0] = selectedAirport ? selectedAirport.iata : '';
      this.$store.commit('updateTransitStops', stops);
    },

    selectAdditionalTransitStop(index, selectedAirport) {
      const allStops = [this.firstTransitStop, ...this.additionalTransitStops];
      allStops[index + 1] = selectedAirport ? selectedAirport.iata : '';
      this.$store.commit('updateTransitStops', allStops);
    },

    validateAndEmit() {
      const isValid = this.isFormValid;
      this.$emit('validation-change', isValid);
    }
  },

  watch: {
    hasTransitStops(newValue) {
      if (newValue === true && this.$store.state.bookingHome.transitStops.length === 0) {
        // Initialize with one empty transit stop
        this.$store.commit('updateTransitStops', ['']);
        this.selectedAdditionalTransitStops = [];
      } else if (newValue === false || newValue === null) {
        // Clear all transit stops
        this.$store.commit('updateTransitStops', []);
        this.selectedAdditionalTransitStops = [];
        this.selectedFirstTransitStop = null;
      }
      this.validateAndEmit();
    },

    totalTravelers() {
      this.validateAndEmit();
    },

    customsAcceptance() {
      this.validateAndEmit();
    },

    selectedFirstTransitStop() {
      this.validateAndEmit();
    },

    departureAirport() {
    },

    travelersUnder16() {
    },

    travelersAge16() {
    },

    travelersAge17() {
    },

    travelersAge18Plus() {
    },

    customsCode() {
    },

    customsComment() {
    }
  },

  mounted() {
    this.airports = getAirports();

    // Validate that airports data has required fields
    if (!validateAirportsData(this.airports)) {
      return; // Page will reload, no need to continue
    }
    this.euAirports = Object.freeze(this.airports.filter(airport => airport.eucustoms === true));
    
    // Initialize selected airports from store values using IATA codes
    this.selectedDepartureAirport = this.airports.find(airport => 
      airport.iata === this.departureAirport
    ) || null;
    
    this.selectedFirstTransitStop = this.airports.find(airport => 
      airport.iata === this.firstTransitStop
    ) || null;
    
    // Initialize additional transit stops selections using IATA codes
    this.selectedAdditionalTransitStops = this.additionalTransitStops.map(stopIata => 
      this.airports.find(airport => airport.iata === stopIata) || null
    );
    
    // Listen for external EU travel type selection
    document.addEventListener('click', (event) => {
      if (event.target.id === 'eu-travel' || event.target.id === 'non-eu-travel') {
        this.travelType = event.target.id === 'eu-travel' ? 'eu' : 'non-eu';
      }
    });

    // Initial validation
    this.validateAndEmit();
  }
}
</script>

<style lang="scss" scoped>
.customs-service {
  .checkbox-wrapper,   .radio-wrapper {
    display: flex;
    align-items: flex-start;
    margin-left: 10px;
    
    input[type="checkbox"], input[type="radio"] {
      margin-top: 2px;
      margin-right: 8px;
    }
    
    label {
      flex: 1;
      line-height: 1.4;
    }
  }

  .radio-group {
    display: flex;
    flex-direction: column;
  }

  .age-group {
    min-width: 120px;
    
    .input-number {
      width: 60px;
      height: 36px;
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 8px 12px;
      text-align: center;
    }
  }

  .customs-list {
    list-style: decimal;
    padding-left: 20px;
    
    li {
      margin-bottom: 12px;
      line-height: 1.4;
    }
  }

  .text-field-info {
    margin-top: 4px;
  }

  .transit-stop-item {
    .btn-remove-stop {
      background: #dc3545;
      color: white;
      border: none;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover {
        background: #c82333;
      }

      i {
        font-size: 14px;
      }
    }
  }

  .add-more-transit {
    .btn-add-transit {
      background: #007bff;
      color: white;
      border: none;
      border-radius: 4px;
      padding: 8px 16px;
      display: flex;
      align-items: center;
      cursor: pointer;
      font-size: 14px;
      transition: background-color 0.2s;

      &:hover {
        background: #0056b3;
      }

      i {
        margin-right: 4px;
      }
    }
  }

  .non-eu-notice {
    background-color: #FFDF7E;
    border-radius: 4px;
    font-size: 16px;
    line-height: 1.5;
    color: #333;
  }

  .color-red {
    color: #dc3545;
  }
}

.btn-add-transit {
  background: #007bff;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  
  &:hover {
    background: #0056b3;
  }
}

.btn-remove-stop {
  background: #dc3545;
  color: white;
  border: none;
  padding: 4px 6px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-left: auto;
  
  &:hover {
    background: #c82333;
  }
}

.customs-list {
  padding-left: 20px;
  
  li {
    margin-bottom: 8px;
    line-height: 1.4;
  }
}

.checkbox-wrapper {
  display: flex;
  align-items: flex-start;
  
  input[type="checkbox"] {
    margin-top: 2px;
    margin-right: 8px;
  }
  
  label {
    flex: 1;
    line-height: 1.4;
  }
}

.age-group {
  min-width: 150px;
  
  .input-number {
    width: 80px;
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
  }
}

.input-default {
  padding: 17.5px;
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  outline: 0;
}
</style> 
